import { ActiveGameButtonType, ActiveGameType } from '@ps/web-poker-types';
import type { ActiveGameData, ActiveGameDataTourney } from '@ps/web-poker-types';

import type { OpenWindowState } from '../types/types';
import { OpenTableAction } from '../types/types';

export const ACTIVE_TABLE_CHANNEL_NAME = 'active-game-channel';
export const ACTIVE_TABLE_WINDOW_NAME = 'ActiveTable';

export function isTableWindowActive() {
  const activeTableWindow = localStorage.getItem(ACTIVE_TABLE_WINDOW_NAME);
  return !!activeTableWindow;
}

export function setTableWindowActive() {
  localStorage.setItem(ACTIVE_TABLE_WINDOW_NAME, 'true');
}

export function setTableWindowInctive() {
  localStorage.removeItem(ACTIVE_TABLE_WINDOW_NAME);
}

export function openOrFocusTab(url: string): OpenWindowState {
  const isActive = isTableWindowActive();
  const win: Window | null = window.open('', ACTIVE_TABLE_WINDOW_NAME);
  const result: OpenWindowState = {
    win,
    action: OpenTableAction.FOCUS
  };

  if (win?.location.href === 'about:blank') {
    if (isActive) {
      win?.close();
      result.action = OpenTableAction.ALREADY_ACTIVE;
    } else {
      win.location.href = url;
      result.action = OpenTableAction.OPEN;
    }
  }

  return result;
}

/**
 * @description This function checks if the BroadcastChannel API is available in the current environment.
 * It checks if the window object is defined and if the BroadcastChannel constructor is a function.
 * This is useful for determining if the BroadcastChannel API can be used for communication between different tabs or windows.
 * @returns true if BroadcastChannel is available, false otherwise
 * @example
 * const isAvailable = isBroadcastChannelAvailable();
 */
export function isBroadcastChannelAvailable(): boolean {
  return typeof window !== 'undefined' && typeof BroadcastChannel === 'function';
}

/**
 * @description This function retrieves the first active game that allows taking a seat.
 * It filters the games to find those of type Tourney with a button type of TAKE_SEAT,
 * sorts them by inviteReceivedAt in descending order (most recent first), and returns the first one.
 * Games without inviteReceivedAt are deprioritized and placed after games with inviteReceivedAt.
 * If no such game exists, it returns undefined.
 * @param games - Array of active game data.
 * @returns The active game with the most recent invite that allows taking a seat, or undefined if none exists.
 * @example
 * const takeSeatGame = getTakeSeatGame(activeGames);
 */
export function getTakeSeatGame(games: ActiveGameData[]): ActiveGameDataTourney | undefined {
  const canTakeSeatGames = games.filter(
    (game) =>
      game.type === ActiveGameType.Tourney && game.buttonType === ActiveGameButtonType.TAKE_SEAT
  ) as ActiveGameDataTourney[];

  canTakeSeatGames.sort(
    (a, b) =>
      // Sort by inviteReceivedAt in descending order, games without inviteReceivedAt appear last
      (b.inviteReceivedAt ?? 0) - (a.inviteReceivedAt ?? 0)
  );

  return canTakeSeatGames.length > 0 ? canTakeSeatGames[0] : undefined;
}
